import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Play,
  Sparkles,
  Archive,
  Image,
  RotateCcw,
  AlertTriangle,
  Camera,
  Download,
} from 'lucide-react';
import type { AdminWorldDetail } from '../../../lib/api';
import { adminApi } from '../../../lib/api';

interface WorldOperationsProps {
  world: AdminWorldDetail;
  onUpdate: () => Promise<void>;
}

export function WorldOperations({ world, onUpdate }: WorldOperationsProps) {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-xl text-parchment">Operations</h1>
        <p className="text-mist text-sm mt-1">Administrative actions for this world</p>
      </div>

      {/* Operations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GenerateNowCard worldId={world.id} onUpdate={onUpdate} />
        <ThumbnailCard worldId={world.id} />
        <WorldVisualCard world={world} />
        <EvolveWorldCard worldId={world.id} />
        <CompactWorldCard worldId={world.id} />
        <TokenRotationCard world={world} onUpdate={onUpdate} />
        <WorldExportCard world={world} />
      </div>

      {/* Danger Zone */}
      <div className="mt-8">
        <DangerZone worldId={world.id} onDelete={() => navigate('/admin/worlds')} />
      </div>
    </div>
  );
}

function WorldExportCard({ world }: { world: AdminWorldDetail }) {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`/api/admin/worlds/${world.id}/export`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error ?? 'Failed to export world');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      const fallbackName = `world-${world.inboundToken}-export.zip`;
      const filenameMatch = contentDisposition?.match(/filename=\"?([^\";]+)\"?/i);
      const filename = filenameMatch?.[1] ?? fallbackName;

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setSuccess('Export downloaded.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export world');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <OperationCard
      icon={Download}
      title="Export World"
      description="Download a full export (JSON + media) for backup or transfer."
    >
      <button
        onClick={handleExport}
        disabled={isExporting}
        className="w-full px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isExporting ? 'Preparing...' : 'Download Export'}
      </button>
      {error && <div className="text-ember text-xs mt-2">{error}</div>}
      {success && <div className="text-success text-xs mt-2">{success}</div>}
    </OperationCard>
  );
}

function OperationCard({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: typeof Play;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-night border border-stone/30 rounded-xl p-4">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-rune/20 rounded-lg">
          <Icon size={20} className="text-rune" />
        </div>
        <div>
          <h3 className="font-display text-parchment">{title}</h3>
          <p className="text-mist text-xs mt-1">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function GenerateNowCard({ worldId, onUpdate }: { worldId: string; onUpdate: () => Promise<void> }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminApi.triggerGeneration(worldId, 'Admin triggered generation');
      setSuccess(`Generation job queued for ${result.jobDate}`);
      setTimeout(async () => {
        await onUpdate();
        setIsGenerating(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger generation');
      setIsGenerating(false);
    }
  };

  return (
    <OperationCard
      icon={Play}
      title="Generate Now"
      description="Trigger letter generation immediately, bypassing the scheduled delivery time."
    >
      <button
        onClick={handleGenerate}
        disabled={isGenerating}
        className="w-full px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isGenerating ? 'Processing...' : 'Generate Now'}
      </button>
      {error && <div className="text-ember text-xs mt-2">{error}</div>}
      {success && <div className="text-success text-xs mt-2">{success}</div>}
    </OperationCard>
  );
}

function ThumbnailCard({ worldId }: { worldId: string }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminApi.generateWorldThumbnail(worldId, 'manual');
      setSuccess(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to queue thumbnail');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <OperationCard
      icon={Image}
      title="Refresh Thumbnail"
      description="Queue a new thumbnail render from the latest world state summaries."
    >
      <button
        onClick={handleGenerate}
        disabled={isGenerating}
        className="w-full px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isGenerating ? 'Queueing...' : 'Regenerate Thumbnail'}
      </button>
      {error && <div className="text-ember text-xs mt-2">{error}</div>}
      {success && <div className="text-success text-xs mt-2">{success}</div>}
    </OperationCard>
  );
}

function WorldVisualCard({ world }: { world: AdminWorldDetail }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const visualUrl = world.latestState?.worldVisualUrl ?? null;

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminApi.generateWorldVisual(world.id, 'manual');
      setSuccess(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to queue world visual');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <OperationCard
      icon={Camera}
      title="Generate World Visual"
      description="Create a high-fidelity scene image that captures the current world revision."
    >
      {visualUrl ? (
        <div className="mb-3">
          <img
            src={visualUrl}
            alt="World visual"
            className="w-full rounded-lg border border-stone/20"
          />
        </div>
      ) : (
        <div className="text-mist text-xs mb-3">No world visual generated yet.</div>
      )}
      <button
        onClick={handleGenerate}
        disabled={isGenerating}
        className="w-full px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isGenerating ? 'Queueing...' : 'Generate World Visual'}
      </button>
      {error && <div className="text-ember text-xs mt-2">{error}</div>}
      {success && <div className="text-success text-xs mt-2">{success}</div>}
    </OperationCard>
  );
}

function EvolveWorldCard({ worldId }: { worldId: string }) {
  const [isEvolving, setIsEvolving] = useState(false);
  const [reason, setReason] = useState('');
  const [focus, setFocus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleEvolve = async () => {
    if (!reason.trim()) return;
    setIsEvolving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminApi.evolveWorld(worldId, reason, focus.trim() || undefined);
      setSuccess(result.message);
      setReason('');
      setFocus('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger world evolution');
    } finally {
      setIsEvolving(false);
    }
  };

  return (
    <OperationCard
      icon={Sparkles}
      title="Evolve World"
      description="Run the world builder to expand canon and side events without needing a new letter."
    >
      <div className="space-y-2">
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for evolution (required)"
          className="w-full px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
        />
        <input
          type="text"
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          placeholder="Optional focus (e.g., factions, timeline)"
          className="w-full px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
        />
        <button
          onClick={handleEvolve}
          disabled={isEvolving || !reason.trim()}
          className="w-full px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isEvolving ? 'Queueing...' : 'Evolve World'}
        </button>
      </div>
      {error && <div className="text-ember text-xs mt-2">{error}</div>}
      {success && <div className="text-success text-xs mt-2">{success}</div>}
    </OperationCard>
  );
}

function CompactWorldCard({ worldId }: { worldId: string }) {
  const [isCompacting, setIsCompacting] = useState(false);
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleCompact = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for compaction');
      return;
    }
    setIsCompacting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminApi.compactWorld(worldId, reason.trim(), force);
      setSuccess(result.message);
      setReason('');
      setForce(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger compaction');
    } finally {
      setIsCompacting(false);
    }
  };

  return (
    <OperationCard
      icon={Archive}
      title="Archive & Compact"
      description="Create an archive snapshot and compress canon for long-running worlds."
    >
      <div className="space-y-2">
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for compaction (required)"
          className="w-full px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
        />
        <label className="flex items-center gap-2 text-xs text-mist/70">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            className="rounded border-stone/30 bg-void"
          />
          Force compaction even if thresholds are not met.
        </label>
        <button
          onClick={handleCompact}
          disabled={isCompacting || !reason.trim()}
          className="w-full px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isCompacting ? 'Queueing...' : 'Run Compaction'}
        </button>
      </div>
      {error && <div className="text-ember text-xs mt-2">{error}</div>}
      {success && <div className="text-success text-xs mt-2">{success}</div>}
    </OperationCard>
  );
}

function TokenRotationCard({ world, onUpdate }: { world: AdminWorldDetail; onUpdate: () => Promise<void> }) {
  const [tokenPreview, setTokenPreview] = useState<string | null>(null);
  const [tokenPreviewLoading, setTokenPreviewLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isWorldEmpty = world.recentLettersIn.length === 0 && world.recentLettersOut.length === 0;

  const handlePreview = async () => {
    setTokenPreviewLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminApi.previewWorldTokenRotation(world.id);
      setTokenPreview(result.proposedToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate token');
    } finally {
      setTokenPreviewLoading(false);
    }
  };

  const handleRotate = async () => {
    if (!tokenPreview || !reason.trim()) {
      setError('Please provide a reason and generate a new token first');
      return;
    }
    if (!window.confirm('Rotate this world token? This is only safe before any letters are received or sent. Continue?')) {
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminApi.rotateWorldToken(world.id, {
        token: tokenPreview,
        reason: reason.trim(),
      });
      setSuccess(result.message);
      setReason('');
      setTokenPreview(null);
      await onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate token');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <OperationCard
      icon={RotateCcw}
      title="Rotate Token"
      description="Generate a new correspondent token. Only available before any letters are exchanged."
    >
      <div className="text-xs text-mist/70 mb-2">
        Current token: <span className="text-parchment font-mono">{world.inboundToken}</span>
      </div>
      {!isWorldEmpty && (
        <div className="text-ember text-xs bg-ember/10 rounded px-3 py-2 mb-3">
          Disabled: this world already has letters.
        </div>
      )}
      <div className="space-y-2">
        <button
          onClick={handlePreview}
          disabled={tokenPreviewLoading || !isWorldEmpty}
          className="w-full px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg text-sm hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {tokenPreviewLoading ? 'Generating...' : 'Generate New Token'}
        </button>
        {tokenPreview && (
          <>
            <div className="text-xs text-mist/70">
              Proposed token: <span className="text-parchment font-mono">{tokenPreview}</span>
            </div>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for rotation (required)"
              className="w-full px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
            />
            <button
              onClick={handleRotate}
              disabled={isSaving || !reason.trim() || !isWorldEmpty}
              className="w-full px-4 py-2 bg-ember/20 text-ember border border-ember/30 rounded-lg text-sm hover:bg-ember/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Rotating...' : 'Apply Token Rotation'}
            </button>
          </>
        )}
      </div>
      {error && <div className="text-ember text-xs mt-2">{error}</div>}
      {success && <div className="text-success text-xs mt-2">{success}</div>}
    </OperationCard>
  );
}

function DangerZone({ worldId, onDelete }: { worldId: string; onDelete: () => void }) {
  const [reason, setReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!reason.trim()) return;
    if (!window.confirm('This will permanently delete the world and ALL related data. This action cannot be undone.\n\nContinue?')) {
      return;
    }
    setIsDeleting(true);
    try {
      const result = await adminApi.deleteWorld(worldId, reason.trim());
      alert(result.message);
      onDelete();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete world');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-night border border-ember/30 rounded-xl p-4">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-ember/20 rounded-lg">
          <AlertTriangle size={20} className="text-ember" />
        </div>
        <div>
          <h3 className="font-display text-ember">Danger Zone</h3>
          <p className="text-mist text-xs mt-1">
            Permanently delete this world and all associated letters, states, and embeddings.
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for deletion (required)..."
          className="flex-1 px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-ember"
        />
        <button
          onClick={handleDelete}
          disabled={isDeleting || !reason.trim()}
          className="px-4 py-2 bg-ember/20 text-ember border border-ember/30 rounded-lg text-sm hover:bg-ember/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
        >
          {isDeleting ? 'Deleting...' : 'Delete World'}
        </button>
      </div>
    </div>
  );
}
